import type { CategoryRoom, PackageOptionId } from '../packageOptions/types';

import { packageKey, round2 } from './forecastingModel';

export const MATERIALISATION_PCT_OVER = 100;
export const MATERIALISATION_PCT_HEALTHY = 80;
export const MATERIALISATION_PCT_LOW = 50;

// CMs spell freesale many ways in this free-text field. It means no cap, so materialisation has
// no denominator by design and a percentage here would be invented.
const FREESALE_PATTERN = /free\s*s(ale|ell)|site\s*minder|travelclick/i;

export type AllocationState =
  | { kind: 'set'; rooms: number }
  | { kind: 'freesale' }
  | { kind: 'unset' }
  | { kind: 'unparseable'; text: string };

export interface MaterialisationRow {
  roomId: PackageOptionId;
  assetName: string;
  allocation: AllocationState;
  forecastedPackages: number;
  forecastedRoomNights: number;
  allocatedRoomNights: number | null;
  materialisationPct: number | null;
}

export type MaterialisationRoom = Pick<CategoryRoom, 'id' | 'assetId' | 'assetName'>;

export interface MaterialisationInput {
  rooms: Array<MaterialisationRoom>;
  allocationByRoomId: Record<PackageOptionId, string | undefined>;
  packagesByAssetLos: Record<string, number>;
  lengthsOfStay: Array<number>;
  availableDays: number | null;
  expectedAvgLos: number | null;
}

export function readAllocation(raw: string | undefined): AllocationState {
  const text = (raw ?? '').trim();

  if (text === '') {
    return { kind: 'unset' };
  }

  if (FREESALE_PATTERN.test(text)) {
    return { kind: 'freesale' };
  }

  // Deliberately whole-string, not a leading-integer parse. "20 Sun - Thu / 7 Fri-Sat" would yield
  // 20 and overstate the weekend, and a wrong denominator reads exactly like a right one.
  if (!/^\d+$/.test(text)) {
    return { kind: 'unparseable', text };
  }

  return { kind: 'set', rooms: Number(text) };
}

export function computeMaterialisation({
  rooms,
  allocationByRoomId,
  packagesByAssetLos,
  lengthsOfStay,
  availableDays,
  expectedAvgLos,
}: MaterialisationInput): Array<MaterialisationRow> {
  const seenAssets = new Set<string>();

  const base = rooms.flatMap((room) => {
    const { assetId } = room;

    // Two base rows can point at one asset, and both would read the same forecast cells.
    if (!assetId || seenAssets.has(assetId)) {
      return [];
    }
    seenAssets.add(assetId);

    const packagesFor = (nights: number) =>
      Math.max(0, Math.round(Number(packagesByAssetLos[packageKey(assetId, nights)]) || 0));

    return [
      {
        assetName: room.assetName,
        forecastedPackages: lengthsOfStay.reduce((total, nights) => total + packagesFor(nights), 0),
        roomId: room.id,
        roomNights: lengthsOfStay.reduce((total, nights) => total + packagesFor(nights) * nights, 0),
      },
    ];
  });

  const totalPackages = base.reduce((total, row) => total + row.forecastedPackages, 0);
  const baseRoomNights = base.reduce((total, row) => total + row.roomNights, 0);
  const baseAvgLos = totalPackages > 0 ? baseRoomNights / totalPackages : null;

  // A longer expected stay holds the room for more nights, so the grid alone under-reports every
  // room. The guard stays on the overall average to match the summary's roomNights, and the share is
  // per package so a room taking more of the forecast takes more of the extra nights.
  const extraNightsTotal =
    expectedAvgLos !== null && baseAvgLos !== null && expectedAvgLos > baseAvgLos
      ? (expectedAvgLos - baseAvgLos) * totalPackages
      : 0;

  return base.map(({ assetName, forecastedPackages, roomId, roomNights }) => {
    const forecastedRoomNights = round2(
      roomNights + (totalPackages > 0 ? extraNightsTotal * (forecastedPackages / totalPackages) : 0),
    );

    const allocation = readAllocation(allocationByRoomId[roomId]);
    const allocatedRoomNights =
      allocation.kind === 'set' && availableDays !== null ? allocation.rooms * availableDays : null;

    return {
      allocatedRoomNights,
      allocation,
      assetName,
      forecastedPackages,
      forecastedRoomNights,
      materialisationPct:
        allocatedRoomNights !== null && allocatedRoomNights > 0
          ? (forecastedRoomNights / allocatedRoomNights) * 100
          : null,
      roomId,
    };
  });
}
